// Import Dependencies
import React, { Component, Fragment } from 'react';
import { reaction } from 'mobx';
import { observer, inject } from 'mobx-react';
import { withRouter, Link } from 'react-router-dom';

// Import Components
import Stepper from '~/common/components/Stepper';
import Centering from '~/common/components/Centering';
import BackButton from '~/common/components/BackButton';
import Button from '~/common/components/CommonButton';
import HeaderGroup from '~/common/components/HeaderGroup';
import LoadingSpinner from '~/common/components/LoadingSpinner';


import { scBeacon, createPersonalizedPurchaseEvent, gtm, clickCtaEvent, viewContentEvent } from '~/common/utils/tracking.js';
import {
    FLOW_STEP_NUMBERS,
    PATH_PLANS, PATH_NUMBERS, PATH_TRIAL_SIGNUP
} from '~/common/utils/consts.js';

// Import Stylesheet
import styles from './confirm-number-transfer.css';

@withRouter
@inject('lockNumberStore', 'tollFreeNumbersStore', 'transferNumberStore', 'progressStore', 'trialSignupStore')
@observer
export default class ConfirmNumberTransfer extends Component {
    componentDidMount() {
        window.scroll({ top: 0, left: 0, behavior: 'smooth' });

        const { tollFreeNumbersStore, trialSignupStore } = this.props;

        const { isTrialFlow } = trialSignupStore;
        gtm(viewContentEvent(isTrialFlow ? 'trial flow' : 'buy flow', 'numbers', 'port number'));

        if (!tollFreeNumbersStore.fetchedTollFreeNumbers) tollFreeNumbersStore.getTollFreeNumbers();
    }

    lockSelectedNumber(ev) {
        ev.preventDefault();

        const { lockNumberStore, tollFreeNumbersStore } = this.props;

        // set selected number in store
        lockNumberStore.setSelectedNumber(tollFreeNumbersStore.trialValidNumber);
        // set selected number type to Transfer
        lockNumberStore.setSelectedNumberType('Transfer');
        // lock number to session, go on to the next page
        lockNumberStore.lockNumberToSession('transfer');

        // track SCBeacon: selected own number for Transfer
        scBeacon(createPersonalizedPurchaseEvent('1'));
    }

    goToNextPage = reaction(
        () => this.props.lockNumberStore.numberIsLocked && this.props.transferNumberStore.transferNumber,
        () => {
            if (
                this.props.lockNumberStore.numberIsLocked &&
                this.props.transferNumberStore.transferNumber
            ) {
                this.props.history.push(this.props.trialSignupStore.isTrialFlow ?
                    PATH_TRIAL_SIGNUP
                    :
                    this.props.progressStore.nextStepFromNumbers
                );
            }
        }
    );

    render() {
        const { tollFreeNumbersStore, trialSignupStore } = this.props;

        return (
            <main className={styles.wrapper}>
                <Centering relative={true}>
                    <Stepper currentStep={FLOW_STEP_NUMBERS} />
                    <BackButton currentStep={FLOW_STEP_NUMBERS} />
                    <HeaderGroup>
                        <h1>Use your current number.</h1>
                        <h2>Bring your existing business number to Grasshopper, with no extra fee.</h2>
                    </HeaderGroup>
                    {
                        tollFreeNumbersStore.fetchedTollFreeNumbers ?
                            <Fragment>
                                <h3 className={styles['temporary-number']}>
                                    Temporary number: <br />{tollFreeNumbersStore.trialValidNumber.UI}
                                </h3>
                                <p className={`${styles.instructions} ${styles['instructions--emphasis']}`}>
                                    {trialSignupStore.isTrialFlow ?
                                        'This is your temporary Grasshopper number for use during your free trial.'
                                        :
                                        'This is a temporary Grasshopper number to use until your port has gone through.'
                                    }
                                </p>
                                <Button
                                    id="confirm-transfer__continue"
                                    modifier={styles['continue-cta']}
                                    onClick={ev => this.lockSelectedNumber(ev)}
                                >Continue</Button>
                                <Link
                                    className={styles['change-number']}
                                    id="confirm-transfer__change-number"
                                    onClick={() => gtm(clickCtaEvent("Don't port number", PATH_NUMBERS, 'number transfer'))}
                                    to={PATH_NUMBERS}
                                >
                                    Actually, I don't want to port my number &#187;
                                </Link>
                                <p className={`${styles.details} ${!trialSignupStore.isTrialFlow ? styles['details--more-bottom-spacing'] : ''}`}>
                                    <strong className={styles['detail-highlight']}>Reminder: </strong>
                                    {trialSignupStore.isTrialFlow ?
                                        <Fragment>
                                            You will receive a form to begin the porting process once you upgrade to the paid version of Grasshopper.
                                            To port your number immediately, you can
                                            <Link
                                                className={styles.link}
                                                onClick={() => gtm(clickCtaEvent('skip your trial and buy today', PATH_PLANS, 'number transfer'))}
                                                to={PATH_PLANS}
                                            > skip your trial and buy today</Link>.
                                        </Fragment>
                                        :
                                        <Fragment>
                                            Upon checkout, you will receive an email initiating the porting process.
                                            We'll give you a temporary number to use to set up your account while your port request is processing.
                                        </Fragment>
                                    }
                                </p>
                            </Fragment>

                            :
                            <LoadingSpinner className={styles.spinner} />
                    }
                </Centering>
            </main>
        );
    }
}
